import { ReactComponent as DocIcon } from 'assets/icons/document.svg'
import { ReactComponent as RestrictedIcon } from 'assets/icons/private16.svg'
import cx from 'classnames'
import { useLastEditedFormatted } from 'common/Prose/hooks/useLastEditedFormatted'
import { IReactNodeViewContext } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeViewContext'
import { preserveSelection } from 'common/Prose/utils'
import { mentionsConversionOptions } from 'common/Prose/utils/mentionsConversionOptions'
import { MentionSubject, SmartLinkAttributes } from 'common/Prose/utils/smartLinks/types'
import { MentionsFormatBar } from 'components/MentionsFormatBar/MentionsFormatBar'
import { useArticle } from 'hooks/queries/articles/useArticle'
import { useEffect, useMemo, useState } from 'react'
import Styles from './ArticleSmartLink.module.scss'
import { type EditableSmartLinkChildProps } from './EditableSmartLink'
import GeneralStyles from './EditableSmartLink.module.scss'

export const ArticleSmartLink = (props: EditableSmartLinkChildProps) => {
  const {
    sanitizedHTML,
    nodeViewContext,
    dataProps,
    handleLinkClick,
    showMentionFormatBar,
    setShowMentionFormatBar,
    parentRef,
  } = props
  const { node, getPos, view } = nodeViewContext as IReactNodeViewContext
  const attrs = node.attrs
  const { key, url, title } = attrs as SmartLinkAttributes

  const [mentionSubject, setMentionSubject] = useState<MentionSubject>({
    title: title,
    avatar: { emoji: <DocIcon /> },
  })

  const oneMinute = 60 * 1000
  const {
    data: articleData,
    isError,
    isLoading,
    isLoadingError,
  } = // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useArticle(key!, { staleTime: oneMinute, refetchInterval: oneMinute })

  useEffect(() => {
    if (articleData) {
      const { header_emoji, title: remoteTitle, last_edited_by, last_edited_at } = articleData.article

      if (title !== remoteTitle) {
        const tx = view.state.tr.setNodeMarkup(getPos(), undefined, { ...attrs, title: remoteTitle })
        preserveSelection(tx, view.state)
        view.dispatch(tx)
      }

      setMentionSubject({
        avatar: { emoji: header_emoji ?? <DocIcon className="docEmoji" /> },
        title: remoteTitle,
        lastEditedAt: last_edited_at,
        lastEditedBy: last_edited_by || undefined,
      })
    }
  }, [articleData, attrs, getPos, view, title])

  useEffect(() => {
    if (!isError && !isLoadingError) return

    setMentionSubject((prevMentionSubject) => ({
      title: prevMentionSubject.title,
      avatar: { emoji: <RestrictedIcon className="restrictedIcon" /> },
    }))
  }, [isError, isLoadingError])

  const displayTitle = useMemo(() => mentionSubject?.title || title, [mentionSubject?.title, title])

  const lastEdited = useLastEditedFormatted(mentionSubject)

  const subtitle = useMemo(() => {
    if (isError) return 'You do not have access to this doc.'
    if (isLoading) return 'Loading...'
    return lastEdited
  }, [isError, isLoading, lastEdited])

  return (
    <span className={Styles.articleLinkContainer}>
      {showMentionFormatBar && (
        <MentionsFormatBar
          view={view}
          close={() => setShowMentionFormatBar(false)}
          getPos={getPos}
          mentionSubject={mentionSubject}
          subtitle={subtitle}
          smartLinkUuid={dataProps['data-uuid']}
          link={url}
          node={node}
          onLinkClick={handleLinkClick}
          conversionOptions={mentionsConversionOptions(node, getPos(), view, displayTitle)}
          parentRef={parentRef}
          forceFormatBarVisible={setShowMentionFormatBar}
          linkPosition={{ from: getPos(), to: getPos() + node.nodeSize }}
        />
      )}
      <DocIcon className={GeneralStyles.leadingIcon} />
      <a
        data-test-id="smart-link-editable"
        {...dataProps}
        className={cx({
          [Styles.showingMentionFormatBar]: showMentionFormatBar,
        })}
        href={url}
        onClick={handleLinkClick}
        tabIndex={-1}
        rel="noreferrer"
        dangerouslySetInnerHTML={sanitizedHTML}
      ></a>
    </span>
  )
}
